import {
  agentActivity,
  placeAgentCursor
} from "./chunk-P5UNJCZG.js";
import {
  VideoLightbox,
  VideoPlayer,
  cn,
  formatTime,
  menuPanel,
  useMediaFacts,
  usePlayback
} from "./chunk-GZNI65HQ.js";

// src/Canvas.tsx
import { createContext, useContext, memo as memo4, Suspense, useCallback as useCallback3, useEffect as useEffect4, useMemo, useRef as useRef4, useState as useState4 } from "react";
import {
  Background,
  BackgroundVariant,
  Handle,
  NodeResizer,
  Position,
  ReactFlow,
  useNodesState,
  useStore as useStore4
} from "@xyflow/react";

// src/ZoomBridge.tsx
import { useEffect } from "react";
import { useOnViewportChange, useReactFlow } from "@xyflow/react";
function ZoomBridge({ onApi, onZoom }) {
  const rf = useReactFlow();
  useEffect(() => {
    onApi?.({
      zoomIn: () => {
        void rf.zoomIn({ duration: 150 });
      },
      zoomOut: () => {
        void rf.zoomOut({ duration: 150 });
      },
      reset: () => {
        void rf.zoomTo(1, { duration: 150 });
      },
      fit: () => {
        void rf.fitView({ padding: 0.25, duration: 200 });
      },
      fitSelection: () => {
        const nodes = rf.getNodes().filter((node) => node.selected);
        if (nodes.length) void rf.fitView({ nodes, padding: 0.25, duration: 200 });
      },
      toFlow: (x, y) => rf.screenToFlowPosition({ x, y })
    });
    onZoom?.(rf.getZoom());
    return () => onApi?.(null);
  }, [rf, onApi, onZoom]);
  useOnViewportChange({ onChange: (vp) => onZoom?.(vp.zoom) });
  return null;
}

// src/scene.ts
import { frameOrder } from "@genieartinc/schemas/graph";
var isCutout = (frame) => Boolean(frame.params.small || frame.params.foot);
var FULL_FROM = 300;
function documentExtent(frames) {
  let e = null;
  for (const f of frames) {
    if (!e) e = { minX: f.x, minY: f.y, maxX: f.x + f.w, maxY: f.y + f.h };
    else {
      e.minX = Math.min(e.minX, f.x);
      e.minY = Math.min(e.minY, f.y);
      e.maxX = Math.max(e.maxX, f.x + f.w);
      e.maxY = Math.max(e.maxY, f.y + f.h);
    }
  }
  return e;
}
function minZoomFor(extent, viewport = { width: 1200, height: 800 }, padding = 0.85) {
  if (!extent) return 0.3;
  const w = Math.max(1, extent.maxX - extent.minX), h = Math.max(1, extent.maxY - extent.minY);
  const fit = Math.min(viewport.width / w, viewport.height / h) * padding;
  return Math.max(5e-3, Math.min(0.3, fit));
}
function sampleField(field, u, v) {
  const x = Math.min(field.width - 1, Math.max(0, Math.round(u * field.width)));
  const y = Math.min(field.height - 1, Math.max(0, Math.round(v * field.height)));
  return field.data[(y * field.width + x) * 4] / 255;
}
function footOf(frame) {
  const foot = frame.params.foot;
  if (foot) return { x: frame.x + foot[0], y: frame.y + foot[1] };
  return { x: frame.x + frame.w / 2, y: frame.y + frame.h * 0.92 };
}
function depthAtFoot(frame, field, fieldFrame) {
  if (!field || !fieldFrame) return null;
  const foot = footOf(frame);
  return sampleField(field, (foot.x - fieldFrame.x) / fieldFrame.w, (foot.y - fieldFrame.y) / fieldFrame.h);
}
function orderOf(frame, field, fieldFrame) {
  if (frame.params.background) return -1e6;
  if (frame.params.foot === void 0 && frame.params.depth === void 0 && frame.params.layer === void 0) return 0;
  return frameOrder(frame.params, depthAtFoot(frame, field, fieldFrame));
}
function fieldFrameOf(frames) {
  return frames.find((f) => f.params.fields?.depth && f.params.background) ?? frames.find((f) => f.params.fields?.depth) ?? null;
}
function pickLevel(levels, screenWidth) {
  if (!levels.length) return null;
  const sorted = [...levels].sort((a, b) => a.width - b.width);
  return sorted.find((l) => l.width >= screenWidth) ?? sorted[sorted.length - 1];
}
function hitMapFromImageData(data) {
  const alpha = new Uint8ClampedArray(data.width * data.height);
  for (let i = 0; i < alpha.length; i++) alpha[i] = data.data[i * 4 + 3];
  return { width: data.width, height: data.height, alpha };
}
function opaqueNear(map, px, py, r = 2) {
  for (let y = Math.max(0, py - r); y <= Math.min(map.height - 1, py + r); y++)
    for (let x = Math.max(0, px - r); x <= Math.min(map.width - 1, px + r); x++)
      if (map.alpha[y * map.width + x] > 40) return true;
  return false;
}
function hitsFrame(frame, map, x, y) {
  if (x < frame.x || x >= frame.x + frame.w || y < frame.y || y >= frame.y + frame.h) return false;
  if (!map) return true;
  const px = Math.min(map.width - 1, Math.floor((x - frame.x) / frame.w * map.width));
  const py = Math.min(map.height - 1, Math.floor((y - frame.y) / frame.h * map.height));
  return opaqueNear(map, px, py);
}
function framesUnder(frames, maps, order, x, y) {
  const under = frames.filter((f) => !f.params.background && x >= f.x && x < f.x + f.w && y >= f.y && y < f.y + f.h).sort((a, b) => order(b) - order(a));
  const passthrough = [];
  for (const f of under) {
    if (hitsFrame(f, maps(f), x, y)) return { hit: f, passthrough };
    passthrough.push(f.id);
  }
  return { hit: null, passthrough };
}
function visibleRect(transform, width, height, step = 512) {
  const [tx, ty, k] = transform;
  const q = (v, up) => (up ? Math.ceil(v / step) : Math.floor(v / step)) * step;
  return { x0: q(-tx / k, false), y0: q(-ty / k, false), x1: q((width - tx) / k, true), y1: q((height - ty) / k, true) };
}
var intersects = (r, x, y, w, h) => x < r.x1 && x + w > r.x0 && y < r.y1 && y + h > r.y0;

// src/scene-hooks.tsx
import { memo, useEffect as useEffect2, useRef, useState } from "react";
import { useReactFlow as useReactFlow2, useStore } from "@xyflow/react";
import { jsx, jsxs } from "react/jsx-runtime";
function decode(url) {
  return new Promise((resolve) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = im.naturalWidth;
        c.height = im.naturalHeight;
        const g = c.getContext("2d", { willReadFrequently: true });
        if (!g) return resolve(null);
        g.drawImage(im, 0, 0);
        resolve(g.getImageData(0, 0, c.width, c.height));
      } catch {
        resolve(null);
      }
    };
    im.onerror = () => resolve(null);
    im.src = url;
  });
}
var fields = /* @__PURE__ */ new Map();
function useField(url) {
  const [field, setField] = useState(null);
  useEffect2(() => {
    if (!url) {
      setField(null);
      return;
    }
    let live = true;
    let pending = fields.get(url);
    if (!pending) {
      pending = decode(url).then((d) => d ? { width: d.width, height: d.height, data: d.data } : null);
      fields.set(url, pending);
    }
    void pending.then((f) => {
      if (live) setField(f);
    });
    return () => {
      live = false;
    };
  }, [url]);
  return field;
}
var hitMaps = /* @__PURE__ */ new Map();
function hitMapFor(url) {
  if (hitMaps.has(url)) return hitMaps.get(url);
  hitMaps.set(url, null);
  void decode(url).then((d) => hitMaps.set(url, d ? hitMapFromImageData(d) : null));
  return void 0;
}
function HitBridge({ frames, order, onPassthrough }) {
  const rf = useReactFlow2();
  const pane = useStore((s) => s.domNode);
  const framesRef = useRef(frames);
  framesRef.current = frames;
  const orderRef = useRef(order);
  orderRef.current = order;
  const last = useRef("");
  useEffect2(() => {
    if (!pane) return;
    const move = (e) => {
      if (e.buttons) return;
      const cutouts = framesRef.current.filter((f) => f.params.small);
      if (!cutouts.length) {
        if (last.current) {
          last.current = "";
          onPassthrough([]);
        }
        return;
      }
      const p = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const { passthrough } = framesUnder(cutouts, (f) => hitMapFor(f.params.small), orderRef.current, p.x, p.y);
      const key = passthrough.join(",");
      if (key !== last.current) {
        last.current = key;
        onPassthrough(passthrough);
      }
    };
    const leave = () => {
      if (last.current) {
        last.current = "";
        onPassthrough([]);
      }
    };
    pane.addEventListener("pointermove", move);
    pane.addEventListener("pointerleave", leave);
    return () => {
      pane.removeEventListener("pointermove", move);
      pane.removeEventListener("pointerleave", leave);
    };
  }, [pane, rf, onPassthrough]);
  return null;
}
var GROUNDS_ID = "__grounds__";
var GroundsLayer = memo(function GroundsLayer2({ data }) {
  const key = useStore((s) => {
    const r = visibleRect(s.transform, s.width, s.height);
    return `${r.x0},${r.y0},${r.x1},${r.y1},${s.transform[2].toFixed(3)}`;
  });
  const [x0, y0, x1, y1, zoom] = key.split(",").map(Number);
  const rect = { x0, y0, x1, y1 };
  return /* @__PURE__ */ jsx("div", { className: "pointer-events-none relative h-full w-full", children: data.frames.map((f) => {
    const g = f.params.ground;
    if (!g) return null;
    const gx = f.x + g.box[0], gy = f.y + g.box[1];
    if (!intersects(rect, gx, gy, g.box[2], g.box[3])) return null;
    const src = !g.small || zoom * g.box[2] > FULL_FROM ? g.image : g.small;
    return /* @__PURE__ */ jsx("img", { src, alt: "", draggable: false, className: "absolute max-w-none", style: { left: gx - data.origin.x, top: gy - data.origin.y, width: g.box[2], height: g.box[3], opacity: g.strength ?? 1 } }, f.id);
  }) });
});
function PyramidPicture({ frame, title }) {
  const levels = frame.params.pyramid ?? [];
  const want = useStore((s) => pickLevel(levels, s.transform[2] * frame.w * (typeof window === "undefined" ? 1 : window.devicePixelRatio || 1))?.image ?? "");
  const [shown, setShown] = useState("");
  return /* @__PURE__ */ jsxs("div", { className: "relative h-full w-full", children: [
    shown && shown !== want && /* @__PURE__ */ jsx("img", { src: shown, alt: "", draggable: false, className: "absolute inset-0 h-full w-full" }),
    want && /* @__PURE__ */ jsx("img", { src: want, alt: title, draggable: false, onLoad: () => setShown(want), className: "absolute inset-0 h-full w-full", style: shown && shown !== want ? { opacity: 0 } : void 0 }, want)
  ] });
}

// src/AgentCursor.tsx
import { memo as memo2, useCallback, useSyncExternalStore } from "react";
import { useStore as useStore2 } from "@xyflow/react";
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var samePlacement = (a, b) => a.x === b.x && a.y === b.y && a.docked === b.docked && a.offscreen === b.offscreen && a.flip === b.flip;
var ActiveCursor = memo2(function ActiveCursor2({ activity }) {
  const selector = useCallback((state) => {
    const rects = activity.frameIds.flatMap((id) => {
      const node = state.nodeLookup.get(id);
      if (!node || node.hidden) return [];
      return [{ ...node.internals.positionAbsolute, width: node.measured.width ?? node.width ?? 0, height: node.measured.height ?? node.height ?? 0 }];
    });
    return placeAgentCursor(rects, activity.point, state);
  }, [activity.frameIds, activity.point]);
  const placement = useStore2(selector, samePlacement);
  const status = activity.label + (activity.frameIds.length > 1 ? ` \xB7 ${activity.frameIds.length} frames` : "") + (placement.offscreen ? " \xB7 offscreen" : "");
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: "genie-agent-presence",
      "data-docked": placement.docked || void 0,
      "data-flip": placement.flip || void 0,
      style: { transform: `translate(${placement.x}px, ${placement.y}px)` },
      role: "status",
      "aria-live": "polite",
      "aria-atomic": "true",
      "aria-label": `Genie: ${status}`,
      children: [
        !placement.docked && /* @__PURE__ */ jsxs2(Fragment, { children: [
          /* @__PURE__ */ jsx2("span", { className: "genie-agent-halo", "aria-hidden": "true" }),
          /* @__PURE__ */ jsx2("svg", { className: "genie-agent-pointer", width: "25", height: "30", viewBox: "0 0 25 30", "aria-hidden": "true", children: /* @__PURE__ */ jsx2("path", { d: "M2 2.5 21.5 15 13 17.2 9 26.5Z", fill: "currentColor", stroke: "white", strokeWidth: "2", strokeLinejoin: "round" }) })
        ] }),
        /* @__PURE__ */ jsxs2("span", { className: "genie-agent-tag", "aria-hidden": "true", style: placement.docked ? { maxWidth: Math.min(280, Math.max(80, placement.x - 12)) } : void 0, children: [
          /* @__PURE__ */ jsxs2("span", { className: "genie-agent-name", children: [
            /* @__PURE__ */ jsx2("span", { className: "genie-agent-dot" }),
            "Genie"
          ] }),
          /* @__PURE__ */ jsx2("span", { className: "genie-agent-action", children: status })
        ] })
      ]
    }
  );
});
function AgentCursor({ canvasId }) {
  const subscribe = useCallback((listener) => agentActivity.subscribe(canvasId, listener), [canvasId]);
  const snapshot = useCallback(() => agentActivity.get(canvasId), [canvasId]);
  const activity = useSyncExternalStore(subscribe, snapshot, snapshot);
  return activity ? /* @__PURE__ */ jsx2(ActiveCursor, { activity }) : null;
}

// src/AudioPlayer.tsx
import { memo as memo3, useCallback as useCallback2, useEffect as useEffect3, useRef as useRef2, useState as useState2 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var PEAK_COUNT = 480;
var cache = /* @__PURE__ */ new Map();
var PLAYING_EVENT = "genie:audio-playing";
function peaksOf(buffer, count) {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i));
  const size = Math.max(1, Math.floor(buffer.length / count));
  const peaks = new Float32Array(count);
  let loudest = 0;
  for (let i = 0; i < count; i += 1) {
    let peak = 0;
    const start = i * size;
    for (const data of channels) for (let j = start; j < start + size && j < data.length; j += 1) peak = Math.max(peak, Math.abs(data[j]));
    peaks[i] = peak;
    loudest = Math.max(loudest, peak);
  }
  if (loudest > 0) for (let i = 0; i < count; i += 1) peaks[i] /= loudest;
  return peaks;
}
function decode2(src) {
  let entry = cache.get(src);
  if (!entry) {
    entry = (async () => {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Audio unavailable (${response.status})`);
      const blob = await response.blob();
      const context = new OfflineAudioContext(1, 1, 44100);
      const buffer = await context.decodeAudioData(await blob.arrayBuffer());
      return { blobUrl: URL.createObjectURL(blob), peaks: peaksOf(buffer, PEAK_COUNT), duration: buffer.duration };
    })();
    entry.catch(() => cache.delete(src));
    cache.set(src, entry);
  }
  return entry;
}
var clock = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};
function bars(peaks, count) {
  return Array.from({ length: count }, (_, i) => {
    if (!peaks) return 0.18 + 0.12 * Math.abs(Math.sin(i * 1.7));
    const from = Math.floor(i / count * peaks.length);
    const to = Math.max(from + 1, Math.floor((i + 1) / count * peaks.length));
    let peak = 0;
    for (let j = from; j < to; j += 1) peak = Math.max(peak, peaks[j]);
    return Math.max(0.06, peak);
  });
}
var PlayIcon = () => /* @__PURE__ */ jsx3("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "currentColor", "aria-hidden": "true", children: /* @__PURE__ */ jsx3("path", { d: "M8 5.5v13a1 1 0 0 0 1.53.85l10.4-6.5a1 1 0 0 0 0-1.7L9.53 4.65A1 1 0 0 0 8 5.5Z" }) });
var PauseIcon = () => /* @__PURE__ */ jsxs3("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "currentColor", "aria-hidden": "true", children: [
  /* @__PURE__ */ jsx3("rect", { x: "6", y: "5", width: "4", height: "14", rx: "1.2" }),
  /* @__PURE__ */ jsx3("rect", { x: "14", y: "5", width: "4", height: "14", rx: "1.2" })
] });
var AudioPlayer = memo3(function AudioPlayer2({ src, title = "Audio", className }) {
  const audio = useRef2(null);
  const wave = useRef2(null);
  const [decoded, setDecoded] = useState2(null);
  const [failed, setFailed] = useState2(false);
  const [playing, setPlaying] = useState2(false);
  const [time, setTime] = useState2(0);
  const [hover, setHover] = useState2(null);
  const [count, setCount] = useState2(48);
  const scrubbing = useRef2(false);
  const duration = decoded?.duration ?? audio.current?.duration ?? 0;
  useEffect3(() => {
    let live = true;
    setDecoded(null);
    setFailed(false);
    setTime(0);
    setPlaying(false);
    decode2(src).then((result) => {
      if (live) setDecoded(result);
    }).catch(() => {
      if (live) setFailed(true);
    });
    return () => {
      live = false;
    };
  }, [src]);
  useEffect3(() => {
    const element = wave.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setCount(Math.max(12, Math.floor((entry?.contentRect.width ?? 240) / 5))));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect3(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      if (audio.current) setTime(audio.current.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);
  useEffect3(() => {
    const others = (event) => {
      if (event.detail !== audio.current) audio.current?.pause();
    };
    window.addEventListener(PLAYING_EVENT, others);
    return () => window.removeEventListener(PLAYING_EVENT, others);
  }, []);
  const toggle = useCallback2(() => {
    const element = audio.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => setPlaying(false));
    else element.pause();
  }, []);
  const seek = (fraction) => {
    const element = audio.current;
    if (!element || !duration) return;
    element.currentTime = Math.min(Math.max(fraction, 0), 1) * duration;
    setTime(element.currentTime);
  };
  const fractionAt = (clientX) => {
    const rect = wave.current?.getBoundingClientRect();
    return rect?.width ? (clientX - rect.left) / rect.width : 0;
  };
  const down = (event) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    scrubbing.current = true;
    seek(fractionAt(event.clientX));
  };
  const move = (event) => {
    const fraction = Math.min(Math.max(fractionAt(event.clientX), 0), 1);
    setHover(fraction);
    if (scrubbing.current) seek(fraction);
  };
  const keys = (event) => {
    const step = event.shiftKey ? 10 : 2;
    const next = event.key === "ArrowLeft" ? time - step : event.key === "ArrowRight" ? time + step : event.key === "Home" ? 0 : event.key === "End" ? duration : null;
    if (next === null || !duration) {
      if (event.key === " ") {
        event.preventDefault();
        toggle();
      }
      return;
    }
    event.preventDefault();
    seek(next / duration);
  };
  const progress = duration ? time / duration : 0;
  const heights = bars(decoded?.peaks ?? null, count);
  const extension = /\.([a-z0-9]+)(?:[?#]|$)/i.exec(src)?.[1]?.toUpperCase();
  return /* @__PURE__ */ jsxs3("div", { className: cn("flex h-full w-full flex-col justify-center gap-3 bg-card px-4 py-3", className), children: [
    /* @__PURE__ */ jsx3(
      "audio",
      {
        ref: audio,
        src: decoded?.blobUrl ?? (failed ? src : void 0),
        preload: "metadata",
        onPlay: () => {
          setPlaying(true);
          window.dispatchEvent(new CustomEvent(PLAYING_EVENT, { detail: audio.current }));
        },
        onPause: () => {
          setPlaying(false);
          if (audio.current) setTime(audio.current.currentTime);
        },
        onEnded: () => {
          setPlaying(false);
          setTime(0);
          if (audio.current) audio.current.currentTime = 0;
        },
        onTimeUpdate: (event) => {
          if (!playing) setTime(event.currentTarget.currentTime);
        }
      }
    ),
    /* @__PURE__ */ jsxs3(
      "div",
      {
        ref: wave,
        role: "slider",
        tabIndex: 0,
        "aria-label": `Seek ${title}`,
        "aria-valuemin": 0,
        "aria-valuemax": Math.round(duration),
        "aria-valuenow": Math.round(time),
        "aria-valuetext": `${clock(time)} of ${clock(duration)}`,
        onPointerDown: down,
        onPointerMove: move,
        onPointerUp: () => {
          scrubbing.current = false;
        },
        onPointerCancel: () => {
          scrubbing.current = false;
        },
        onPointerLeave: () => setHover(null),
        onKeyDown: keys,
        className: "nodrag nopan relative flex h-2/5 min-h-10 w-full cursor-pointer touch-none items-center gap-[2px] rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
        children: [
          heights.map((height, i) => {
            const at = (i + 0.5) / heights.length;
            return /* @__PURE__ */ jsx3(
              "span",
              {
                "aria-hidden": "true",
                className: cn(
                  "min-w-0 flex-1 rounded-full transition-colors duration-150",
                  !decoded && !failed && "animate-pulse",
                  at <= progress ? "bg-foreground" : hover !== null && at <= hover ? "bg-foreground/45" : "bg-foreground/20"
                ),
                style: { height: `${Math.round(height * 100)}%` }
              },
              i
            );
          }),
          duration > 0 && /* @__PURE__ */ jsx3("span", { "aria-hidden": "true", className: "pointer-events-none absolute inset-y-[-4px] w-px bg-foreground", style: { left: `${progress * 100}%` } }),
          hover !== null && duration > 0 && /* @__PURE__ */ jsx3("span", { "aria-hidden": "true", className: "pointer-events-none absolute -top-5 -translate-x-1/2 rounded bg-foreground px-1 py-px text-[9px] tabular-nums text-background", style: { left: `${hover * 100}%` }, children: clock(hover * duration) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx3(
        "button",
        {
          type: "button",
          onClick: (event) => {
            event.stopPropagation();
            toggle();
          },
          onPointerDown: (event) => event.stopPropagation(),
          "aria-label": playing ? `Pause ${title}` : `Play ${title}`,
          disabled: !decoded && !failed,
          className: "nodrag flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-foreground text-background transition-transform hover:scale-105 active:scale-95 disabled:cursor-default disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          children: playing ? /* @__PURE__ */ jsx3(PauseIcon, {}) : /* @__PURE__ */ jsx3("span", { className: "translate-x-px", children: /* @__PURE__ */ jsx3(PlayIcon, {}) })
        }
      ),
      /* @__PURE__ */ jsxs3("span", { className: "text-[11px] tabular-nums text-foreground", children: [
        clock(time),
        /* @__PURE__ */ jsxs3("span", { className: "text-muted-foreground", children: [
          " / ",
          clock(duration)
        ] })
      ] }),
      /* @__PURE__ */ jsx3("span", { className: "ml-auto text-[10px] uppercase tracking-wider text-muted-foreground", children: failed ? "no waveform" : extension ?? "audio" })
    ] })
  ] });
});

// src/media/CanvasVideoFrame.tsx
import { useLayoutEffect, useRef as useRef3, useState as useState3 } from "react";
import { useStore as useStore3 } from "@xyflow/react";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var MICRO_WIDTH = 120;
var FULL_WIDTH = 480;
function CanvasVideoFrame({ src, title = "Video", selected = false, gradient, loop = false }) {
  const box = useRef3(null);
  const [width, setWidth] = useState3(0);
  const [large, setLarge] = useState3(false);
  const zoom = useStore3((state) => Math.round(state.transform[2] * 10) / 10);
  const facts = useMediaFacts(src);
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const onScreen = width * zoom;
  const density = onScreen > 0 && onScreen < MICRO_WIDTH ? "micro" : onScreen > FULL_WIDTH ? "full" : "compact";
  return /* @__PURE__ */ jsxs4("div", { ref: box, className: "h-full w-full", children: [
    /* @__PURE__ */ jsx4(
      VideoPlayer,
      {
        src,
        title,
        poster: facts?.poster ?? void 0,
        gradient,
        density,
        defer: !facts,
        durationHint: facts?.duration,
        loop,
        barPinned: selected && density !== "micro",
        objectFit: "contain",
        onOpenLarge: () => setLarge(true)
      }
    ),
    /* @__PURE__ */ jsx4(VideoLightbox, { src, title, open: large, onClose: () => setLarge(false) })
  ] });
}

// src/Canvas.tsx
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var Extensions = createContext({});
var AppNodeRenderer = memo4(function AppNodeRenderer2({ data }) {
  const host = useContext(Extensions);
  return host.renderTool?.(data.artboard.id) ?? /* @__PURE__ */ jsx5("div", { role: "status", className: "rounded border border-border bg-card p-3 text-xs", children: "This tool needs a connected runtime." });
});
function mediaKind(version) {
  if (version?.mediaType) return version.mediaType;
  if (/\.(mp4|webm|mov)(?:[?#]|$)/i.test(version?.image ?? "")) return "video";
  if (/\.(wav|mp3|ogg)(?:[?#]|$)/i.test(version?.image ?? "")) return "audio";
  if (/\.(glb|gltf|obj|stl|fbx|ply)(?:[?#]|$)/i.test(version?.image ?? "")) return "mesh";
  return "image";
}
var VersionMenu = memo4(function VersionMenu2({ artboard: a, onPick, onDelete }) {
  const host = useContext(Extensions);
  const [open, setOpen] = useState4(false);
  const ref = useRef4(null);
  useEffect4(() => {
    if (!open) return;
    const close = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  return /* @__PURE__ */ jsxs5("div", { ref, className: "nodrag nowheel pointer-events-auto relative", children: [
    /* @__PURE__ */ jsx5(
      "button",
      {
        "aria-label": `Version history \u2014 v${a.activeVersion + 1} of ${a.versions.length}`,
        type: "button",
        title: `Version history \u2014 v${a.activeVersion + 1} of ${a.versions.length}`,
        onClick: (e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        },
        className: cn("flex cursor-pointer items-center rounded p-0.5 transition-colors hover:text-foreground focus:outline-none", open ? "text-foreground" : "text-muted-foreground/60"),
        children: /* @__PURE__ */ jsxs5("svg", { width: "11", height: "11", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: [
          /* @__PURE__ */ jsx5("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
          /* @__PURE__ */ jsx5("path", { d: "M3 3v5h5" }),
          /* @__PURE__ */ jsx5("path", { d: "M12 7v5l4 2" })
        ] })
      }
    ),
    open && // Beside the frame, not under it: the list clears the media and the toolbar
    // below it, and lines up with the top of the picture.
    /* @__PURE__ */ jsx5("div", { className: cn(menuPanel, "absolute left-full z-30 ml-2 max-h-56 w-40 overflow-y-auto !p-0.5"), style: { top: 24 }, children: a.versions.map((v, i) => ({ v, i })).reverse().map(({ v, i }) => /* @__PURE__ */ jsxs5(
      "button",
      {
        "aria-pressed": Boolean(i === a.activeVersion),
        type: "button",
        ref: i === a.activeVersion ? (el) => el?.scrollIntoView({ block: "nearest" }) : void 0,
        onClick: (e) => {
          e.stopPropagation();
          onPick(a.id, i);
          setOpen(false);
        },
        className: cn("flex w-full cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-left text-[11px] transition-colors", i === a.activeVersion ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"),
        children: [
          /* @__PURE__ */ jsx5("span", { className: "h-5 w-5 shrink-0 overflow-hidden rounded border border-border", style: { background: v.gradient }, children: v.image && (mediaKind(v) === "mesh" ? host.renderModelThumb?.(v.image) ?? /* @__PURE__ */ jsx5("span", { className: "flex h-full w-full items-center justify-center text-[8px] text-muted-foreground", children: "3D" }) : /* @__PURE__ */ jsx5("img", { src: v.image, alt: "", className: "h-full w-full object-cover", draggable: false })) }),
          /* @__PURE__ */ jsxs5("span", { className: "min-w-0 flex-1 truncate", children: [
            /* @__PURE__ */ jsxs5("span", { className: "tabular-nums text-muted-foreground/50", children: [
              "v",
              i + 1
            ] }),
            " ",
            v.label
          ] }),
          onDelete && a.versions.length > 1 && /* @__PURE__ */ jsx5(
            "span",
            {
              role: "button",
              title: "Delete this version",
              onClick: (e) => {
                e.stopPropagation();
                onDelete(a.id, v.id);
              },
              className: "shrink-0 rounded px-1 text-[10px] leading-none text-muted-foreground/40 hover:bg-accent hover:text-red-400",
              children: "\xD7"
            }
          )
        ]
      },
      v.id
    )) })
  ] });
});
function JobOverlay({ job, onAction }) {
  const btn = "nodrag pointer-events-auto cursor-pointer rounded border border-white/20 bg-black/60 px-1.5 py-0.5 text-[9px] text-white/80 hover:bg-black/80 hover:text-white";
  if (job.status === "error" || job.status === "cancelled") {
    return /* @__PURE__ */ jsxs5(Fragment2, { children: [
      /* @__PURE__ */ jsx5("span", { className: cn("px-3 text-center text-[10px]", job.status === "error" ? "text-red-400/90" : "text-muted-foreground"), children: job.status === "cancelled" ? "stopped" : (job.error ?? "failed").slice(0, 80) }),
      onAction && /* @__PURE__ */ jsx5("button", { type: "button", onClick: (e) => {
        e.stopPropagation();
        onAction(job.id, "retry");
      }, className: btn, children: "retry" })
    ] });
  }
  return /* @__PURE__ */ jsxs5(Fragment2, { children: [
    job.preview && /* @__PURE__ */ jsx5("img", { src: job.preview, alt: "", draggable: false, className: "absolute inset-0 h-full w-full object-cover opacity-80" }),
    /* @__PURE__ */ jsxs5("span", { className: "relative z-10 flex items-center gap-1.5 rounded bg-black/50 px-1.5 py-0.5 text-[11px]", children: [
      job.status === "running" ? `rendering ${Math.round(job.progress * 100)}%` : "queued\u2026",
      onAction && /* @__PURE__ */ jsx5("button", { type: "button", "aria-label": "Stop", title: "Stop", onClick: (e) => {
        e.stopPropagation();
        onAction(job.id, "cancel");
      }, className: btn + " !px-1 leading-none", children: "\xD7" })
    ] }),
    /* @__PURE__ */ jsx5("div", { className: "relative z-10 h-0.5 w-2/3 overflow-hidden rounded-full bg-muted/70", children: /* @__PURE__ */ jsx5("div", { className: "h-full bg-neutral-200 transition-[width] duration-500", style: { width: `${Math.round(job.progress * 100)}%` } }) })
  ] });
}
var ArtboardNode = memo4(function ArtboardNode2({ data, selected }) {
  const host = useContext(Extensions);
  const { artboard: a, onPick, onDeleteVersion, onText, onRename, onResize, onDims, onAutoLayout, onEditDesign, onJobAction, onExpand, onUpscale, previewFilter, dropTarget } = data;
  const [altDown, setAltDown] = useState4(false);
  const [fill, setFill] = useState4(null);
  useEffect4(() => {
    const down = (e) => {
      if (e.key === "Alt") setAltDown(true);
    };
    const up = (e) => {
      if (e.key === "Alt") setAltDown(false);
    };
    const blur = () => setAltDown(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);
  const hasPicture = a.kind === "media" && Boolean(a.versions[a.activeVersion]?.image) && mediaKind(a.versions[a.activeVersion]) === "image";
  const cutout = isCutout(a);
  const small = a.params.small;
  const full = useStore4((s) => !small || s.transform[2] * a.w > FULL_FROM);
  const backdrop = Boolean(a.params.background);
  const locked = Boolean(a.params.locked);
  const padsFor = useCallback3((p) => {
    const pxW = a.params.width ?? 1024;
    const scale = pxW / a.w;
    const snap = (n) => Math.max(0, Math.round(n * scale / 8) * 8);
    const pads = {
      left: snap(a.x - p.x),
      top: snap(a.y - p.y),
      right: snap(p.x + p.width - (a.x + a.w)),
      bottom: snap(p.y + p.height - (a.y + a.h))
    };
    return pads.left + pads.right + pads.top + pads.bottom > 0 ? pads : null;
  }, [a.params.width, a.w, a.h, a.x, a.y]);
  const [editingDims, setEditingDims] = useState4(null);
  const [entered, setEntered] = useState4(false);
  useEffect4(() => {
    if (!selected && entered) setEntered(false);
    if (!entered) return;
    const onEsc = (e) => {
      if (e.key === "Escape") setEntered(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [selected, entered]);
  const pw = a.params.width;
  const ph = a.params.height;
  const px = pw && ph ? `${pw} \xD7 ${ph}` : `${Math.round(a.w)} \xD7 ${Math.round(a.h)}`;
  const [capped, setCapped] = useState4(false);
  useEffect4(() => {
    if (!selected) setCapped(false);
  }, [selected]);
  if (a.kind === "note") {
    return /* @__PURE__ */ jsxs5("div", { className: "group relative h-full w-full", children: [
      /* @__PURE__ */ jsx5(
        NodeResizer,
        {
          isVisible: Boolean(selected),
          minWidth: 160,
          minHeight: 120,
          lineClassName: "genie-resize-line",
          handleClassName: "genie-resize-handle",
          onResizeEnd: (_, p) => onResize?.(a.id, Math.round(p.width), Math.round(p.height))
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute -top-5 left-0 right-0 flex items-center gap-1.5 px-0.5", children: [
        /* @__PURE__ */ jsxs5("svg", { width: "11", height: "11", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", className: "shrink-0 text-muted-foreground/70", "aria-hidden": true, children: [
          /* @__PURE__ */ jsx5("path", { d: "M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z" }),
          /* @__PURE__ */ jsx5("path", { d: "M15 3v6h6" })
        ] }),
        /* @__PURE__ */ jsx5(FrameTitle, { a, onRename, className: "text-[11px] text-muted-foreground" })
      ] }),
      /* @__PURE__ */ jsx5(
        "div",
        {
          className: cn(
            "flex h-full w-full flex-col overflow-hidden rounded-[2px] border bg-card transition-shadow",
            selected ? "border-transparent shadow-[0_0_0_1px_hsl(var(--border))]" : "border-border group-hover:border-neutral-600"
          ),
          children: /* @__PURE__ */ jsx5(
            "textarea",
            {
              defaultValue: a.params.text ?? "",
              placeholder: "Write a note\u2026",
              "aria-label": a.title || "Note",
              onBlur: (e) => onText?.(a.id, e.target.value),
              onPointerDown: (e) => e.stopPropagation(),
              className: "nodrag nowheel min-h-0 w-full flex-1 resize-none bg-transparent px-3 py-2.5 text-[12px] leading-relaxed text-foreground placeholder:text-muted-foreground/45 focus:outline-none"
            }
          )
        }
      )
    ] });
  }
  if (a.kind === "section") {
    return /* @__PURE__ */ jsxs5("div", { className: "relative h-full w-full rounded-[2px] border border-border/80 bg-white/[0.02]", children: [
      /* @__PURE__ */ jsx5(
        NodeResizer,
        {
          isVisible: Boolean(selected),
          minWidth: 240,
          minHeight: 160,
          lineClassName: "genie-resize-line",
          handleClassName: "genie-resize-handle",
          onResizeEnd: (_, p) => onResize?.(a.id, Math.round(p.width), Math.round(p.height))
        }
      ),
      /* @__PURE__ */ jsx5(FrameTitle, { a, onRename, className: "absolute -top-5 left-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground" }),
      selected && /* @__PURE__ */ jsx5(
        "button",
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            onAutoLayout?.(a.id);
          },
          className: "nodrag absolute -top-6 right-0 cursor-pointer rounded bg-[#4c9ffb] px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-[#3d8ceb]",
          children: "auto layout"
        }
      )
    ] });
  }
  if (a.kind === "design") {
    const img = a.versions[a.activeVersion]?.image;
    return /* @__PURE__ */ jsxs5("div", { className: "group relative h-full w-full", onDoubleClick: () => onEditDesign?.(a.id), children: [
      /* @__PURE__ */ jsx5(
        NodeResizer,
        {
          isVisible: Boolean(selected),
          minWidth: 120,
          minHeight: 90,
          keepAspectRatio: true,
          lineClassName: "genie-resize-line",
          handleClassName: "genie-resize-handle",
          onResizeEnd: (_, p) => onResize?.(a.id, Math.round(p.width), Math.round(p.height))
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute -top-5 left-0 right-0 flex items-center justify-between px-0.5", children: [
        /* @__PURE__ */ jsx5("span", { className: "text-[11px] text-muted-foreground", children: a.title }),
        /* @__PURE__ */ jsx5(
          "button",
          {
            type: "button",
            onClick: () => onEditDesign?.(a.id),
            className: "pointer-events-auto cursor-pointer text-[10px] text-muted-foreground/70 hover:text-foreground",
            children: "edit"
          }
        )
      ] }),
      img ? /* @__PURE__ */ jsx5("div", { className: cn("h-full w-full overflow-hidden rounded-[2px] border transition-shadow", selected ? "border-transparent" : "border-border group-hover:border-neutral-600"), children: /* @__PURE__ */ jsx5("img", { src: img, alt: a.title, className: "h-full w-full object-cover", draggable: false }) }) : /* @__PURE__ */ jsxs5("div", { className: cn("flex h-full w-full flex-col items-center justify-center gap-1.5 rounded-[2px] border text-muted-foreground", selected ? "border-transparent" : "border-dashed border-neutral-600 group-hover:border-neutral-400"), children: [
        /* @__PURE__ */ jsx5("span", { className: "text-[11px]", children: "layers" }),
        /* @__PURE__ */ jsx5("span", { className: "text-[9px] opacity-60", children: "double-click to edit layers" })
      ] })
    ] });
  }
  if (a.kind === "composition") {
    return /* @__PURE__ */ jsxs5("div", { className: "group relative h-full w-full", onDoubleClick: () => onEditDesign?.(a.id), children: [
      /* @__PURE__ */ jsx5(
        NodeResizer,
        {
          isVisible: Boolean(selected),
          minWidth: 160,
          minHeight: 90,
          keepAspectRatio: true,
          lineClassName: "genie-resize-line",
          handleClassName: "genie-resize-handle",
          onResizeEnd: (_, p) => onResize?.(a.id, Math.round(p.width), Math.round(p.height))
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute -top-5 left-0 right-0 flex items-center justify-between px-0.5", children: [
        /* @__PURE__ */ jsx5("span", { className: "text-[11px] text-muted-foreground", children: a.title }),
        /* @__PURE__ */ jsx5(
          "button",
          {
            type: "button",
            onClick: () => onEditDesign?.(a.id),
            className: "pointer-events-auto cursor-pointer text-[10px] text-muted-foreground/70 hover:text-foreground",
            children: "edit"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs5("div", { className: cn("h-full w-full overflow-hidden rounded-[2px] border bg-black", dropTarget ? "border-transparent outline outline-2 outline-offset-2 outline-sky-400" : selected ? "border-transparent" : "border-border group-hover:border-neutral-600"), children: [
        dropTarget && /* @__PURE__ */ jsx5("span", { className: "pointer-events-none absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 text-center text-[11px] text-white drop-shadow", children: "Add as a layer" }),
        host.renderComposition?.(a.id, Boolean(selected)) ?? /* @__PURE__ */ jsxs5("div", { className: "flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground", children: [
          /* @__PURE__ */ jsx5("span", { className: "text-[11px]", children: "compositor" }),
          /* @__PURE__ */ jsx5("span", { className: "text-[9px] opacity-60", children: "double-click to compose" })
        ] })
      ] })
    ] });
  }
  if (a.kind === "web") {
    const qs = a.params.values && Object.keys(a.params.values).length ? `?${new URLSearchParams(Object.fromEntries(Object.entries(a.params.values).map(([k, v]) => [k, String(v)]))).toString()}` : "";
    const url = (a.params.url ?? "") + qs;
    const src = host.resolveAppUrl?.(url);
    return /* @__PURE__ */ jsxs5("div", { className: "group relative h-full w-full", children: [
      /* @__PURE__ */ jsx5(
        NodeResizer,
        {
          isVisible: Boolean(selected),
          minWidth: 200,
          minHeight: 150,
          lineClassName: "genie-resize-line",
          handleClassName: "genie-resize-handle",
          onResizeEnd: (_, p) => onResize?.(a.id, Math.round(p.width), Math.round(p.height))
        }
      ),
      /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute -top-5 left-0 right-0 flex items-center justify-between px-0.5", children: [
        /* @__PURE__ */ jsx5("span", { className: "text-[11px] text-muted-foreground", children: a.title }),
        /* @__PURE__ */ jsx5("span", { className: "pointer-events-auto flex items-center gap-2", children: /* @__PURE__ */ jsx5(
          "a",
          {
            href: url,
            target: "_blank",
            rel: "noreferrer",
            className: "text-[10px] text-muted-foreground/70 hover:text-foreground",
            children: "open \u2197"
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs5(
        "div",
        {
          onDoubleClick: () => setEntered(true),
          className: cn("relative h-full w-full overflow-hidden rounded-[2px] border bg-white transition-shadow", selected ? "border-transparent" : "border-border group-hover:border-neutral-600"),
          children: [
            /* @__PURE__ */ jsx5(
              WebAppFrame,
              {
                src,
                title: a.title,
                live: Boolean(a.params.liveValues),
                values: a.params.values,
                className: cn("h-full w-full", entered ? "nodrag nowheel" : "pointer-events-none")
              }
            ),
            entered && /* @__PURE__ */ jsx5("span", { className: "pointer-events-none absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white/80", children: "esc to exit" })
          ]
        }
      )
    ] });
  }
  const active = a.versions[a.activeVersion];
  const expandable = hasPicture && Boolean(onExpand);
  const cap = hasPicture && pw && ph && !(expandable && altDown) ? { w: Math.max(120, pw), h: Math.max(90, ph) } : null;
  return /* @__PURE__ */ jsxs5("div", { className: "group relative h-full w-full", children: [
    /* @__PURE__ */ jsx5(
      NodeResizer,
      {
        isVisible: Boolean(selected) && !locked && !backdrop,
        minWidth: 120,
        minHeight: 90,
        maxWidth: cap?.w,
        maxHeight: cap?.h,
        keepAspectRatio: a.kind === "media" && !(expandable && altDown),
        lineClassName: "genie-resize-line",
        handleClassName: "genie-resize-handle",
        onResize: (_, p) => {
          if (expandable && altDown) setFill(padsFor(p));
          else if (fill) setFill(null);
          if (cap) setCapped(p.width >= cap.w - 1 || p.height >= cap.h - 1);
        },
        onResizeEnd: (_, p) => {
          const pads = expandable && altDown ? padsFor(p) : null;
          setFill(null);
          onResize?.(a.id, Math.round(p.width), Math.round(p.height));
          if (pads) onExpand?.(a.id, pads);
        }
      }
    ),
    selected && capped && cap && pw && ph && /* @__PURE__ */ jsxs5("div", { className: "nodrag absolute -bottom-9 left-0 right-0 z-20 flex items-center justify-center gap-2 whitespace-nowrap text-[10px]", children: [
      /* @__PURE__ */ jsxs5("span", { className: "rounded bg-amber-500/90 px-2 py-1 font-medium text-white shadow", children: [
        pw,
        " \xD7 ",
        ph,
        " px is the source \xB7 larger only goes soft"
      ] }),
      onUpscale && /* @__PURE__ */ jsxs5("span", { className: "flex items-center gap-1 rounded bg-background/95 px-1 py-0.5 shadow", children: [
        /* @__PURE__ */ jsx5("span", { className: "pl-1 text-muted-foreground", children: "make it" }),
        [2, 4].map((f) => /* @__PURE__ */ jsxs5(
          "button",
          {
            type: "button",
            onClick: (e) => {
              e.stopPropagation();
              setCapped(false);
              onUpscale(a.id, f);
            },
            className: "cursor-pointer rounded bg-primary px-1.5 py-0.5 font-medium text-primary-foreground hover:bg-primary/90",
            children: [
              f,
              "\xD7 \xB7 ",
              pw * f,
              " \xD7 ",
              ph * f
            ]
          },
          f
        )),
        /* @__PURE__ */ jsx5("button", { type: "button", onClick: (e) => {
          e.stopPropagation();
          setCapped(false);
        }, className: "cursor-pointer px-1 text-muted-foreground hover:text-foreground", "aria-label": "Dismiss", children: "\xD7" })
      ] })
    ] }),
    fill && /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 rounded bg-[#4c9ffb] px-2 py-1 text-[10px] font-medium tabular-nums text-white shadow", children: [
      "Fill ",
      [fill.left && `\u2190 ${fill.left}`, fill.right && `\u2192 ${fill.right}`, fill.top && `\u2191 ${fill.top}`, fill.bottom && `\u2193 ${fill.bottom}`].filter(Boolean).join(" \xB7 "),
      " px"
    ] }),
    selected && expandable && !fill && /* @__PURE__ */ jsx5("div", { className: "pointer-events-none absolute -bottom-5 right-0 text-[9px] text-muted-foreground/60 opacity-0 transition-opacity group-hover:opacity-100", children: "\u2325-drag an edge to fill outward" }),
    !backdrop && /* @__PURE__ */ jsxs5("div", { className: "pointer-events-none absolute -top-5 left-0 right-0 flex items-center justify-between px-0.5", children: [
      /* @__PURE__ */ jsxs5("span", { className: "flex min-w-0 items-center gap-1 truncate text-[11px] text-muted-foreground", children: [
        a.params.favorite && /* @__PURE__ */ jsx5("span", { className: "shrink-0 text-red-400", children: "\u2665" }),
        locked && /* @__PURE__ */ jsx5("span", { className: "shrink-0", title: a.params.lock?.note ?? "Locked: no batch, tool or drag changes this frame", "aria-label": "Locked", children: "\u{1F512}" }),
        /* @__PURE__ */ jsx5(FrameTitle, { a, onRename })
      ] }),
      /* @__PURE__ */ jsxs5("span", { className: "flex shrink-0 items-center gap-1", children: [
        host.renderFrameActions?.(a.id),
        a.kind === "media" && a.versions.length > 1 && /* @__PURE__ */ jsx5(VersionMenu, { artboard: a, onPick, onDelete: locked ? void 0 : onDeleteVersion })
      ] })
    ] }),
    a.kind === "media" && a.versions.length === 0 ? /* @__PURE__ */ jsx5(
      "div",
      {
        className: cn("relative flex h-full w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-[2px] border text-muted-foreground transition-colors", selected ? "border-transparent" : "border-dashed border-neutral-600 group-hover:border-neutral-400"),
        children: a.job ? /* @__PURE__ */ jsx5(JobOverlay, { job: a.job, onAction: onJobAction }) : /* @__PURE__ */ jsxs5(Fragment2, { children: [
          /* @__PURE__ */ jsx5("span", { className: "text-[11px]", children: "empty frame" }),
          /* @__PURE__ */ jsx5("span", { className: "text-[9px] opacity-60", children: "select \xB7 describe it in chat" })
        ] })
      }
    ) : a.kind === "media" ? /* @__PURE__ */ jsxs5(
      "div",
      {
        className: cn("relative h-full w-full overflow-hidden", !cutout && !backdrop && "rounded-[2px] border transition-shadow", !cutout && !backdrop && (selected ? "border-transparent" : "border-border group-hover:border-neutral-600")),
        style: cutout || backdrop ? void 0 : { background: active.gradient },
        children: [
          a.params.flags?.length ? /* @__PURE__ */ jsx5("span", { className: "absolute right-0 top-0 z-10 h-[5%] w-[5%] min-h-1 min-w-1 bg-red-500/85", title: a.params.flags.join(", "), "aria-label": `Flagged: ${a.params.flags.join(", ")}` }) : null,
          backdrop && a.params.pyramid?.length ? /* @__PURE__ */ jsx5(PyramidPicture, { frame: a, title: a.title }) : active.image && (mediaKind(active) === "video" ? /* @__PURE__ */ jsx5(CanvasVideoFrame, { src: active.image, title: a.title, selected, gradient: active.gradient }) : mediaKind(active) === "audio" ? /* @__PURE__ */ jsx5(AudioPlayer, { src: active.image, title: a.title }) : mediaKind(active) === "mesh" ? (
            /* click selects / drag moves the frame; double-click "enters" the model so orbit never fights the canvas (same as web frames) */
            /* @__PURE__ */ jsxs5("div", { className: "relative h-full w-full bg-card", onDoubleClick: () => setEntered(true), children: [
              /* @__PURE__ */ jsx5("div", { className: cn("h-full w-full", entered && "nodrag nowheel"), children: /* @__PURE__ */ jsx5(Suspense, { fallback: /* @__PURE__ */ jsx5("div", { className: "flex h-full w-full items-center justify-center text-[10px] text-muted-foreground", children: "loading viewer\u2026" }), children: host.renderModel?.(a.id, active.image, entered) ?? /* @__PURE__ */ jsx5("div", { role: "status", className: "p-3 text-xs", children: "The 3D viewer is unavailable in this host. Your model is saved." }) }) }),
              entered ? /* @__PURE__ */ jsx5("span", { className: "pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white/80", children: "orbit \xB7 scroll to zoom \xB7 esc to exit" }) : /* @__PURE__ */ jsx5("span", { className: "pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white/70 opacity-0 transition-opacity group-hover:opacity-100", children: "double-click to orbit" }),
              /* @__PURE__ */ jsx5("a", { href: active.image, download: true, onClick: (e) => e.stopPropagation(), className: "absolute bottom-1.5 right-1.5 rounded bg-background/70 px-1.5 py-0.5 text-[9px] text-muted-foreground backdrop-blur hover:text-foreground", children: ".glb" })
            ] })
          ) : !previewFilter && host.renderImage?.(a.id, active.image, a.title) || /* @__PURE__ */ jsx5("img", { src: cutout && !full && small ? small : active.image, alt: a.title, className: "h-full w-full object-cover", draggable: false, style: previewFilter ? { filter: previewFilter } : void 0 })),
          host.renderPictureOverlay?.(a.id),
          active.error && /* @__PURE__ */ jsx5("div", { role: "alert", className: "absolute inset-0 flex items-center justify-center bg-card p-3 text-xs text-red-700", children: active.error }),
          a.job && /* @__PURE__ */ jsx5("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/45 text-muted-foreground", children: /* @__PURE__ */ jsx5(JobOverlay, { job: a.job, onAction: onJobAction }) })
        ]
      }
    ) : /* @__PURE__ */ jsx5("div", { className: "flex h-full w-full gap-2 rounded-[2px] border border-dashed border-border p-2", children: a.versions.map((v) => /* @__PURE__ */ jsx5("div", { className: "flex-1 rounded-md", style: { background: v.gradient } }, v.id)) }),
    selected && (editingDims ? /* @__PURE__ */ jsxs5("div", { className: "nodrag absolute left-1/2 top-full mt-1.5 flex -translate-x-1/2 items-center gap-1 rounded bg-[#4c9ffb] px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white", children: [
      /* @__PURE__ */ jsx5(
        "input",
        {
          autoFocus: true,
          value: editingDims.w,
          onChange: (e) => setEditingDims({ ...editingDims, w: e.target.value.replace(/\D/g, "") }),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              const W = Number(editingDims.w), H = Number(editingDims.h);
              if (W >= 64 && H >= 64) onDims?.(a.id, W, H);
              setEditingDims(null);
            }
            if (e.key === "Escape") setEditingDims(null);
          },
          className: "w-10 bg-transparent text-center focus:outline-none"
        }
      ),
      "\xD7",
      /* @__PURE__ */ jsx5(
        "input",
        {
          value: editingDims.h,
          onChange: (e) => setEditingDims({ ...editingDims, h: e.target.value.replace(/\D/g, "") }),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              const W = Number(editingDims.w), H = Number(editingDims.h);
              if (W >= 64 && H >= 64) onDims?.(a.id, W, H);
              setEditingDims(null);
            }
            if (e.key === "Escape") setEditingDims(null);
          },
          className: "w-10 bg-transparent text-center focus:outline-none"
        }
      )
    ] }) : /* @__PURE__ */ jsx5(
      "button",
      {
        type: "button",
        "aria-label": "Click to edit resolution",
        title: "Click to edit resolution",
        onClick: (e) => {
          e.stopPropagation();
          setEditingDims({ w: String(pw ?? Math.round(a.w * 3)), h: String(ph ?? Math.round(a.h * 3)) });
        },
        className: "nodrag absolute left-1/2 top-full mt-1.5 -translate-x-1/2 cursor-text rounded bg-[#4c9ffb] px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white",
        children: px
      }
    ))
  ] });
});
var OpNode = memo4(function OpNode2({ data }) {
  return /* @__PURE__ */ jsxs5("div", { className: "rounded-md border border-border bg-card px-3 py-2", children: [
    /* @__PURE__ */ jsx5(Handle, { type: "target", position: Position.Left, className: "!bg-neutral-500" }),
    /* @__PURE__ */ jsx5("div", { className: "text-xs font-medium", children: data.label }),
    /* @__PURE__ */ jsx5("div", { className: "text-[10px] text-muted-foreground", children: data.detail }),
    /* @__PURE__ */ jsx5(Handle, { type: "source", position: Position.Right, className: "!bg-neutral-500" })
  ] });
});
var OutputNode = memo4(function OutputNode2({ data }) {
  return /* @__PURE__ */ jsxs5("div", { className: data.handles ? "w-24" : "w-20", children: [
    data.handles && /* @__PURE__ */ jsx5(Handle, { type: "target", position: Position.Left, className: "!bg-neutral-500" }),
    /* @__PURE__ */ jsx5("div", { className: (data.handles ? "h-16" : "h-20") + " overflow-hidden rounded-[2px] border border-border bg-card", children: data.imageUrl && /* @__PURE__ */ jsx5("img", { src: data.imageUrl, alt: "", className: "h-full w-full object-cover", draggable: false }) }),
    data.handles && /* @__PURE__ */ jsx5("div", { className: "mt-1 truncate text-center text-[10px] text-muted-foreground", children: data.label })
  ] });
});
var nodeTypes = { app: AppNodeRenderer, artboard: ArtboardNode, op: OpNode, output: OutputNode, grounds: GroundsLayer };
var EMPTY_IDS = [];
var NO_EDGES = [];
var PAN_SELECT_BUTTONS = [1, 2];
var OPEN_AT_100 = { minZoom: 1, maxZoom: 1 };
var LAYERABLE = /* @__PURE__ */ new Set(["media", "design"]);
function compositionDropTarget(artboards, dragged) {
  const source = artboards.find((a) => a.id === dragged.id);
  if (!source || !LAYERABLE.has(source.kind)) return null;
  const cx = dragged.x + dragged.w / 2, cy = dragged.y + dragged.h / 2;
  const hit = artboards.filter((a) => a.kind === "composition" && a.id !== dragged.id && cx >= a.x && cx <= a.x + a.w && cy >= a.y && cy <= a.y + a.h);
  return hit.length ? hit[hit.length - 1].id : null;
}
var EMPTY_EXTENSIONS = {};
function Canvas(props) {
  return /* @__PURE__ */ jsx5(Extensions.Provider, { value: props.extensions ?? EMPTY_EXTENSIONS, children: /* @__PURE__ */ jsx5("div", { className: "genie-art-canvas h-full w-full", children: /* @__PURE__ */ jsx5(CanvasContent, { ...props }) }) });
}
function FrameTitle({ a, onRename, className }) {
  const [editing, setEditing] = useState4(false);
  if (!onRename) return /* @__PURE__ */ jsx5("span", { className: cn("truncate", className), children: a.title });
  if (editing) return /* @__PURE__ */ jsx5(
    "input",
    {
      autoFocus: true,
      defaultValue: a.title,
      "aria-label": `Rename ${a.title}`,
      className: cn("nodrag nowheel pointer-events-auto min-w-0 flex-1 rounded border border-border bg-card px-1 text-foreground outline-none", className),
      onPointerDown: (e) => e.stopPropagation(),
      onClick: (e) => e.stopPropagation(),
      onBlur: (e) => {
        const name = e.target.value.trim();
        if (name && name !== a.title) onRename(a.id, name);
        setEditing(false);
      },
      onKeyDown: (e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          e.currentTarget.value = a.title;
          e.currentTarget.blur();
        }
      }
    }
  );
  return /* @__PURE__ */ jsx5(
    "span",
    {
      className: cn("nodrag pointer-events-auto cursor-text truncate hover:text-foreground", className),
      title: "Double-click to rename",
      onDoubleClick: (e) => {
        e.stopPropagation();
        setEditing(true);
      },
      children: a.title
    }
  );
}
function CanvasContent({ canvasId, backgroundColor = "#deded6", onMoveFrames, readOnly = false, mode, tool = "select", lineage, artboards, selectedId, selectedIds, selectionEpoch, onSelect, onPickVersion, onMove, onText, onRename, onResize, onDims, onAutoLayout, onUpscale, onSelectIds, onEditDesign, onDropIntoComposition, onJobAction, onExpand, onDeleteVersion, previewFilter, onContextMenu, onZoomApi, onZoom }) {
  const sectionDrag = useRef4(null);
  const frameDrag = useRef4(null);
  const [dropTarget, setDropTarget] = useState4(null);
  const handleNodeClick = useCallback3((_, n) => {
    if (mode === "frame") onSelect(n.id);
  }, [mode, onSelect]);
  const handleSelectionChange = useCallback3(({ nodes }) => {
    if (mode === "frame") onSelectIds?.(nodes.map((n) => n.id));
  }, [mode, onSelectIds]);
  const fieldFrame = useMemo(() => fieldFrameOf(artboards), [artboards]);
  const field = useField(fieldFrame?.params.fields?.depth?.image);
  const order = useCallback3((f) => orderOf(f, field, fieldFrame), [field, fieldFrame]);
  const minZoom = useMemo(() => minZoomFor(documentExtent(artboards)), [artboards]);
  const [passthrough, setPassthrough] = useState4(EMPTY_IDS);
  const passthroughRef = useRef4(passthrough);
  passthroughRef.current = passthrough;
  const onPassthrough = useCallback3((ids) => setPassthrough(ids.length ? ids : EMPTY_IDS), []);
  const classFor = (a, through) => cn(a.params.background && "genie-background", isCutout(a) && "genie-cutout", through.includes(a.id) && "genie-passthrough") || void 0;
  const [frameNodes, setFrameNodes, onFrameNodesChange] = useNodesState([]);
  useEffect4(() => {
    setFrameNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      const sel = selectedIds ?? (selectedId ? [selectedId] : []);
      const nodes = artboards.map((a) => {
        const existing = prevById.get(a.id);
        const live = existing?.dragging || existing?.resizing;
        const isSelected = existing ? existing.selected : sel.includes(a.id);
        return {
          id: a.id,
          type: a.kind === "tool" ? "app" : "artboard",
          // Keep RF's live position/size while interacting; adopt doc values otherwise.
          position: existing?.dragging ? existing.position : { x: a.x, y: a.y },
          width: live && existing?.width ? existing.width : a.w,
          height: live && existing?.height ? existing.height : a.h,
          data: { artboard: a, selected: sel.includes(a.id), dropTarget: dropTarget === a.id, onPick: onPickVersion, onDeleteVersion, onText, onRename, onResize, onDims, onAutoLayout, onUpscale, onEditDesign, onJobAction, onExpand, previewFilter: previewFilter?.id === a.id ? previewFilter.filter : void 0 },
          // Selected frames rise above their neighbours — otherwise an empty
          // frame's transparent interior steals drags aimed at the frame
          // underneath it (the explicit zIndex used to defeat elevate-on-select).
          // In a scene the order is the frame's layer plus the depth at its foot; a backdrop sits under all.
          zIndex: a.kind === "section" ? -10 : a.params.background ? -1e9 : Math.round(order(a) * 1e3) + (isSelected ? 1e6 : 0),
          // A locked frame stays where it is; a backdrop is never a target.
          draggable: !(a.params.locked || a.params.background),
          selectable: !a.params.background,
          className: classFor(a, passthroughRef.current),
          // React Flow OWNS selection state — never overwrite it on sync, or
          // multi-select ping-pongs into an update loop.
          selected: isSelected
        };
      });
      const grounded = artboards.filter((a) => a.params.ground);
      if (grounded.length) {
        const extent = documentExtent(grounded.map((a) => ({ ...a, x: a.x + (a.params.ground?.box[0] ?? 0), y: a.y + (a.params.ground?.box[1] ?? 0), w: a.params.ground?.box[2] ?? a.w, h: a.params.ground?.box[3] ?? a.h })));
        if (extent) nodes.unshift({
          id: GROUNDS_ID,
          type: "grounds",
          position: { x: extent.minX, y: extent.minY },
          width: extent.maxX - extent.minX,
          height: extent.maxY - extent.minY,
          data: { frames: grounded, origin: { x: extent.minX, y: extent.minY } },
          zIndex: -5e8,
          draggable: false,
          selectable: false,
          className: "genie-grounds"
        });
      }
      return nodes;
    });
  }, [artboards, selectedId, selectedIds, dropTarget, onPickVersion, onDeleteVersion, onText, onRename, onResize, onDims, onAutoLayout, onUpscale, onEditDesign, onJobAction, onExpand, previewFilter, setFrameNodes, order]);
  useEffect4(() => {
    setFrameNodes((prev) => prev.map((n) => {
      if (n.id === GROUNDS_ID) return n;
      const a = n.data.artboard;
      if (!a) return n;
      const className = classFor(a, passthrough);
      return n.className === className ? n : { ...n, className };
    }));
  }, [passthrough, setFrameNodes]);
  const selRef = useRef4(selectedIds);
  selRef.current = selectedIds;
  const flowRef = useRef4(null);
  useEffect4(() => {
    if (selectionEpoch === void 0) return;
    const sel = new Set(selRef.current ?? []);
    setFrameNodes((prev) => prev.map((n) => n.selected === sel.has(n.id) ? n : { ...n, selected: sel.has(n.id) }));
  }, [selectionEpoch, setFrameNodes]);
  const { graphNodes, graphEdges } = useMemo(() => {
    if (!lineage) return { graphNodes: [], graphEdges: [] };
    const opsByArtboard = /* @__PURE__ */ new Map();
    for (const n of lineage.nodes) {
      if (n.kind === "op" && n.artboardId) {
        if (!opsByArtboard.has(n.artboardId)) opsByArtboard.set(n.artboardId, []);
        opsByArtboard.get(n.artboardId).push(n);
      }
    }
    const chains = [...opsByArtboard.entries()].filter(([, ops]) => ops.length > 1);
    const singles = [...opsByArtboard.entries()].filter(([, ops]) => ops.length <= 1);
    const outputs = new Map(lineage.nodes.filter((n) => n.kind === "output").map((n) => [n.id, n]));
    const nodes = [];
    const keepIds = /* @__PURE__ */ new Set();
    chains.forEach(([aid, ops], row) => {
      ops.forEach((op) => {
        keepIds.add(op.id);
        nodes.push({
          id: op.id,
          type: "op",
          position: { x: 40 + (op.idx ?? 0) * 230, y: 40 + row * 150 },
          data: { label: op.label, detail: op.detail ?? "" },
          draggable: false
        });
      });
      const out = outputs.get(`out-${aid}`);
      if (out) {
        keepIds.add(out.id);
        nodes.push({
          id: out.id,
          type: "output",
          position: { x: 60 + ops.length * 230, y: 30 + row * 150 },
          data: { label: out.label, imageUrl: out.imageUrl, handles: true },
          draggable: false
        });
      }
    });
    const baseY = 60 + chains.length * 150;
    singles.forEach(([aid], i) => {
      const out = outputs.get(`out-${aid}`);
      if (out) nodes.push({
        id: out.id,
        type: "output",
        position: { x: 40 + i % 8 * 92, y: baseY + Math.floor(i / 8) * 100 },
        data: { label: out.label, imageUrl: out.imageUrl, handles: false },
        draggable: false
      });
    });
    const edges = (lineage.edges ?? []).filter(([s, t]) => keepIds.has(s) && keepIds.has(t)).map(([s, t]) => ({ id: `${s}-${t}`, source: s, target: t, style: { stroke: "oklch(0.5 0 0)", strokeWidth: 1.5 } }));
    return { graphNodes: nodes, graphEdges: edges };
  }, [lineage]);
  return /* @__PURE__ */ jsxs5(
    ReactFlow,
    {
      nodes: mode === "frame" ? frameNodes : graphNodes,
      edges: mode === "frame" ? NO_EDGES : graphEdges,
      onNodesChange: mode === "frame" ? onFrameNodesChange : void 0,
      nodeTypes,
      onNodeClick: handleNodeClick,
      onSelectionChange: handleSelectionChange,
      multiSelectionKeyCode: "Shift",
      onNodeDragStart: (_, n) => {
        if (mode !== "frame") return;
        const a = artboards.find((x) => x.id === n.id);
        frameDrag.current = onDropIntoComposition && a && LAYERABLE.has(a.kind) ? { id: a.id, from: { x: a.x, y: a.y } } : null;
        if (a?.kind !== "section") {
          sectionDrag.current = null;
          return;
        }
        const inside = artboards.filter((x) => x.id !== a.id && x.x + x.w / 2 >= n.position.x && x.x + x.w / 2 <= n.position.x + a.w && x.y + x.h / 2 >= n.position.y && x.y + x.h / 2 <= n.position.y + a.h);
        sectionDrag.current = { id: a.id, last: { ...n.position }, children: inside.map((x) => x.id) };
      },
      onNodeDrag: (_, n, dragged) => {
        const f = frameDrag.current;
        if (f?.id === n.id) {
          const a = artboards.find((x) => x.id === n.id);
          const over = a && dragged.length <= 1 ? compositionDropTarget(artboards, { id: n.id, x: n.position.x, y: n.position.y, w: a.w, h: a.h }) : null;
          setDropTarget((current) => current === over ? current : over);
        }
        const d = sectionDrag.current;
        if (!d || d.id !== n.id || !d.children.length) return;
        const dx = n.position.x - d.last.x;
        const dy = n.position.y - d.last.y;
        d.last = { ...n.position };
        if (dx || dy) setFrameNodes((prev) => prev.map((node) => d.children.includes(node.id) ? { ...node, position: { x: node.position.x + dx, y: node.position.y + dy } } : node));
      },
      onNodeDragStop: (_, n, dragged) => {
        if (mode !== "frame") return;
        const into = frameDrag.current?.id === n.id ? dropTarget : null;
        const from = frameDrag.current?.from;
        frameDrag.current = null;
        setDropTarget(null);
        if (into && from) {
          setFrameNodes((prev) => prev.map((node) => node.id === n.id ? { ...node, position: { ...from } } : node));
          onDropIntoComposition?.(n.id, into);
          sectionDrag.current = null;
          return;
        }
        const d = sectionDrag.current;
        const moves = new Map(dragged.map((node) => [node.id, { id: node.id, ...node.position }]));
        moves.set(n.id, { id: n.id, ...n.position });
        if (d?.id === n.id) for (const child of frameNodes) {
          if (d.children.includes(child.id)) moves.set(child.id, { id: child.id, ...child.position });
        }
        sectionDrag.current = null;
        if (onMoveFrames) onMoveFrames([...moves.values()]);
        else for (const move of moves.values()) onMove?.(move.id, move.x, move.y);
      },
      onPaneClick: () => onSelect(null),
      onInit: (instance) => {
        flowRef.current = instance;
      },
      onNodeContextMenu: (e, n) => {
        if (mode !== "frame") return;
        e.preventDefault();
        if (!selRef.current?.includes(n.id)) onSelect(n.id);
        onContextMenu?.(n.id, e.clientX, e.clientY);
      },
      onSelectionContextMenu: (e, nodes) => {
        if (mode !== "frame" || !nodes.length) return;
        e.preventDefault();
        const at = flowRef.current?.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const size = (n) => ({ w: n.measured?.width ?? n.width ?? 0, h: n.measured?.height ?? n.height ?? 0 });
        const hit = at && nodes.find((n) => {
          const { w, h } = size(n);
          return at.x >= n.position.x && at.x <= n.position.x + w && at.y >= n.position.y && at.y <= n.position.y + h;
        });
        onContextMenu?.((hit ?? nodes[0]).id, e.clientX, e.clientY);
      },
      fitView: true,
      fitViewOptions: OPEN_AT_100,
      proOptions: { hideAttribution: true },
      nodesDraggable: !readOnly && mode === "frame" && tool === "select",
      nodesConnectable: false,
      deleteKeyCode: null,
      panOnScroll: true,
      zoomOnScroll: false,
      zoomOnPinch: true,
      panOnScrollSpeed: 1,
      zoomActivationKeyCode: "Meta",
      panActivationKeyCode: "Space",
      panOnDrag: tool === "hand" ? true : PAN_SELECT_BUTTONS,
      selectionOnDrag: mode === "frame" && tool === "select",
      minZoom,
      onlyRenderVisibleElements: true,
      children: [
        /* @__PURE__ */ jsx5(Background, { variant: BackgroundVariant.Dots, gap: 24, size: 1, color: backgroundColor }),
        /* @__PURE__ */ jsx5(ZoomBridge, { onApi: onZoomApi, onZoom }),
        mode === "frame" && /* @__PURE__ */ jsx5(AgentCursor, { canvasId }),
        mode === "frame" && /* @__PURE__ */ jsx5(HitBridge, { frames: artboards, order, onPassthrough })
      ]
    },
    mode
  );
}
function WebAppFrame({ src, title, live, values, className }) {
  const frame = useRef4(null);
  const [loaded] = useState4(src);
  const address = live ? loaded ?? src : src;
  const first = useRef4(true);
  useEffect4(() => {
    if (!live) return;
    if (first.current) {
      first.current = false;
      return;
    }
    frame.current?.contentWindow?.postMessage({ type: "genie:values", values: values ?? {} }, "*");
  }, [live, values]);
  return /* @__PURE__ */ jsx5("iframe", { ref: frame, src: address, srcDoc: address ? void 0 : "<p>This app needs a connected runtime.</p>", title, sandbox: "allow-scripts", className });
}
export {
  AgentCursor,
  AudioPlayer,
  Canvas,
  CanvasVideoFrame,
  FULL_FROM,
  VideoLightbox,
  VideoPlayer,
  ZoomBridge,
  compositionDropTarget,
  formatTime,
  minZoomFor,
  pickLevel,
  useMediaFacts,
  usePlayback
};
//# sourceMappingURL=index.js.map